import { KaraokeClip } from '../models/karaoke/KaraokeClip';
import { KARAOKE_TRACK_NUMBER } from '../models/Timeline';
import { IdGenerator } from '../services/IdGenerator';
import {
  resolveOverlaps,
  segmentTokens,
  squashOrphanedSegments,
} from '../services/karaokeSegmentation';

import type { KaraokeConfig } from '../models/karaoke/KaraokeConfig';
import type { TranscriptToken } from '../types';
import type { VideoEditor } from '../VideoEditor';

export const ORPHANED_WORDS_COUNT = 2;

export class KaraokeTrackManager {
  private editor: VideoEditor;

  constructor(editor: VideoEditor) {
    this.editor = editor;
  }

  /**
   * Reconciles the karaoke track with the current state of transcript tokens.
   * Called after transcript track modifications to ensure karaoke clips remain in sync.
   *
   * Algorithm:
   * - Get new state of active transcript tokens
   * - Get current karaoke track
   * - Create list for updated karaoke clips
   * - For each word in new transcript:
   *   - Move word by word to updated karaoke clips
   *   - If last word of current karaoke clip met or word from another karaoke clip id met,
   *     current karaoke clip should be ended and new clip started with new autogenerated Id,
   *     but old clip id should be still used to detect end of clip
   *   - If word that is not part of any karaoke clip is met it can be added to current clip.
   *     If it is the first word in a new clip, this clip gets new id autogenerated
   *   - The clip ids that didn't exist or extended with new words should be stored in arrays
   *     for further post-processing
   * - Start/end offsets should only be carried over if first/last word of clip is preserved
   * - hiddenTokenIds should be preserved, unless token is not in clip anymore
   */
  public reconcileKaraokeTrack(): void {
    // Get current karaoke track
    const karaokeTrack = this.editor.getKaraokeTrack();
    if (!karaokeTrack) {
      return; // No karaoke track to reconcile
    }

    // Get new state of active transcript tokens
    const activeTranscriptTokens = this.editor.getActiveTranscriptNew();
    if (!activeTranscriptTokens || activeTranscriptTokens.length === 0) {
      // No transcript tokens, clear karaoke track
      karaokeTrack.karaokeClips.length = 0;
      return;
    }

    // Build a map of old karaoke clips by token IDs for fast lookup
    const oldClipsByTokenId = this.buildTokenToClipMap(
      karaokeTrack.karaokeClips,
    );

    // Create list for updated karaoke clips
    const updatedClips: KaraokeClip[] = [];
    const newOrExtendedClipIds: string[] = [];
    const modifiedClipIds: string[] = [];

    let currentClipTokens: TranscriptToken[] = [];
    let currentClipId: string | null = null;
    let currentClipConfig: KaraokeConfig | null = null;
    let currentClipHiddenTokenIds: string[] = [];
    let currentClipStartOffset: number | undefined = undefined;
    let currentClipEndOffset: number | undefined = undefined;
    let firstTokenInClipId: string | null = null;
    let lastTokenInClipId: string | null = null;
    let lastTokenOfPreviousClipAdded: boolean = false;

    // Process each token in the new transcript
    for (let i = 0; i < activeTranscriptTokens.length; i++) {
      const token = activeTranscriptTokens[i];
      const oldClip = oldClipsByTokenId.get(token.id);

      // Determine if we need to start a new clip
      const shouldStartNewClip =
        this.shouldStartNewClip(currentClipId, oldClip) ||
        (lastTokenOfPreviousClipAdded && token.type === 'text'); // append punctuation

      if (shouldStartNewClip) {
        // Finalize current clip if it has tokens
        if (currentClipTokens.length > 0) {
          this.finalizeClip(
            updatedClips,
            currentClipTokens,
            currentClipId!,
            currentClipConfig!,
            currentClipHiddenTokenIds,
            currentClipStartOffset,
            currentClipEndOffset,
            firstTokenInClipId,
            lastTokenInClipId,
            oldClipsByTokenId,
            newOrExtendedClipIds,
            modifiedClipIds,
          );
        }

        // Start a new clip
        if (oldClip) {
          // Continue with existing clip
          currentClipId = oldClip.id;
          currentClipConfig = oldClip.karaokeConfig;
          currentClipHiddenTokenIds = [...oldClip.hiddenTranscriptTokenIds];
          firstTokenInClipId = oldClip.transcriptTokens[0]?.id || null;
          lastTokenInClipId =
            oldClip.transcriptTokens[oldClip.transcriptTokens.length - 1]?.id ||
            null;
        } else {
          // Create new clip
          currentClipId = IdGenerator.shortRandomId();
          // todo: logic to choose config?
          currentClipConfig = this.getDefaultKaraokeConfig();
          currentClipHiddenTokenIds = [];
          firstTokenInClipId = null;
          lastTokenInClipId = null;
          newOrExtendedClipIds.push(currentClipId);
        }

        lastTokenOfPreviousClipAdded = false;
        currentClipTokens = [];
        currentClipStartOffset = undefined;
        currentClipEndOffset = undefined;
      }

      // Add token to current clip
      currentClipTokens.push(token);

      // Preserve hidden token IDs if token was in the old clip
      if (oldClip && oldClip.hiddenTranscriptTokenIds.includes(token.id)) {
        if (!currentClipHiddenTokenIds.includes(token.id)) {
          currentClipHiddenTokenIds.push(token.id);
        }
      }

      // Track if this is a new word added to an existing clip
      if (oldClip && !oldClip.transcriptTokens.find(t => t.id === token.id)) {
        if (!newOrExtendedClipIds.includes(currentClipId!)) {
          newOrExtendedClipIds.push(currentClipId!);
        }
      }

      // Update start offset if this is the first token and matches original clip's first token
      if (
        currentClipTokens.length === 1 &&
        oldClip &&
        firstTokenInClipId === token.id
      ) {
        currentClipStartOffset = oldClip.startSecondsOffset;
      }

      // Update end offset if this token matches the original clip's last token
      if (oldClip && lastTokenInClipId === token.id) {
        currentClipEndOffset = oldClip.endSecondsOffset;
        lastTokenOfPreviousClipAdded = true;
      }
    }

    // Finalize the last clip
    if (currentClipTokens.length > 0) {
      this.finalizeClip(
        updatedClips,
        currentClipTokens,
        currentClipId!,
        currentClipConfig!,
        currentClipHiddenTokenIds,
        currentClipStartOffset,
        currentClipEndOffset,
        firstTokenInClipId,
        lastTokenInClipId,
        oldClipsByTokenId,
        newOrExtendedClipIds,
        modifiedClipIds,
      );
    }

    // Post-processing: Split new or extended clips into smaller segments
    if (newOrExtendedClipIds.length > 0) {
      this.splitClipsIntoSegments(updatedClips, newOrExtendedClipIds);
    }

    // Validate and fix overlaps before replacing clips
    resolveOverlaps(updatedClips);

    // Replace karaoke clips with updated clips
    karaokeTrack.karaokeClips = updatedClips;
  }

  /**
   * Builds a map of token IDs to karaoke clips for fast lookup
   */
  private buildTokenToClipMap(clips: KaraokeClip[]): Map<string, KaraokeClip> {
    const map = new Map<string, KaraokeClip>();
    for (const clip of clips) {
      for (const token of clip.transcriptTokens) {
        map.set(token.id, clip);
      }
    }
    return map;
  }

  /**
   * Determines if a new clip should be started based on current state
   */
  private shouldStartNewClip(
    currentClipId: string | null,
    oldClip: KaraokeClip | undefined,
  ): boolean {
    // Start first clip
    if (currentClipId === null) {
      return true;
    }

    // Start new clip if token belongs to a different clip
    if (oldClip && oldClip.id !== currentClipId) {
      return true;
    }

    return false;
  }

  /**
   * Finalizes a clip and adds it to the updated clips array
   */
  private finalizeClip(
    updatedClips: KaraokeClip[],
    tokens: TranscriptToken[],
    clipId: string,
    config: KaraokeConfig,
    hiddenTokenIds: string[],
    startOffset: number | undefined,
    endOffset: number | undefined,
    firstTokenInClipId: string | null,
    lastTokenInClipId: string | null,
    oldClipsByTokenId: Map<string, KaraokeClip>,
    newOrExtendedClipIds: string[],
    modifiedClipIds: string[],
  ): void {
    // Check if first/last words are preserved
    const firstTokenId = tokens[0]?.id;
    const lastTokenId = tokens[tokens.length - 1]?.id;

    const preserveStartOffset = firstTokenInClipId === firstTokenId;
    const preserveEndOffset = lastTokenInClipId === lastTokenId;

    // Filter hidden token IDs to only include tokens still in the clip
    const tokenIdsSet = new Set(tokens.map(t => t.id));
    const filteredHiddenTokenIds = hiddenTokenIds.filter(id =>
      tokenIdsSet.has(id),
    );

    // Check if clip was modified
    const oldClip = oldClipsByTokenId.get(firstTokenId);
    if (oldClip && oldClip.id === clipId) {
      // Check if token list changed
      const oldTokenIds = oldClip.transcriptTokens.map(t => t.id).join(',');
      const newTokenIds = tokens.map(t => t.id).join(',');
      if (oldTokenIds !== newTokenIds) {
        modifiedClipIds.push(clipId);
      }
    }

    // Create new karaoke clip
    const clip = new KaraokeClip({
      id: clipId,
      transcriptTokens: tokens,
      hiddenTranscriptTokenIds: filteredHiddenTokenIds,
      startSecondsOffset: preserveStartOffset ? startOffset : undefined,
      endSecondsOffset: preserveEndOffset ? endOffset : undefined,
      karaokeConfig: config,
      track: KARAOKE_TRACK_NUMBER,
    });

    updatedClips.push(clip);
  }

  /**
   * Gets the default karaoke config for new clips
   */
  private getDefaultKaraokeConfig(): KaraokeConfig {
    // Get default config from editor
    const defaultConfig = this.editor.getDefaultKaraokeConfig();
    if (defaultConfig) {
      return defaultConfig;
    }

    throw new Error(
      'No karaoke configuration available for creating new clips',
    );
  }

  /**
   * Splits clips that are new or extended into smaller segments using karaokeSegmentation
   */
  private splitClipsIntoSegments(
    clips: KaraokeClip[],
    clipIdsToSplit: string[],
  ): void {
    const clipIdsSet = new Set(clipIdsToSplit);

    // Process clips in reverse order to safely replace them
    for (let i = clips.length - 1; i >= 0; i--) {
      const clip = clips[i];

      if (!clipIdsSet.has(clip.id)) {
        continue; // Skip clips that don't need splitting
      }

      // Segment the clip's tokens
      const segments = segmentTokens(clip.transcriptTokens);

      // If segmentation produced only one segment, no splitting needed
      if (segments.length <= 1) {
        continue;
      }

      // Remove the original clip
      clips.splice(i, 1);

      // Create new clips from segments and insert at the same position
      const newClips: KaraokeClip[] = [];
      for (const segment of segments) {
        if (segment.tokens.length === 0) {
          continue;
        }

        // Identify hidden tokens from the original clip
        const hiddenTokenIds = clip.hiddenTranscriptTokenIds.filter(id =>
          segment.tokens.some(t => t.id === id),
        );

        // Create new clip from segment
        const newClip = new KaraokeClip({
          id: IdGenerator.shortRandomId(),
          transcriptTokens: segment.tokens,
          hiddenTranscriptTokenIds: hiddenTokenIds,
          ignoredByFiltersIds: [],
          karaokeConfig: clip.karaokeConfig,
          track: clip.track,
          startSecondsOffset: undefined,
          endSecondsOffset: undefined,
        });

        newClips.push(newClip);
      }

      // Squash orphaned clips within the new clips before inserting
      const squashedClips = squashOrphanedSegments(newClips);

      // Insert squashed clips at the original position
      clips.splice(i, 0, ...squashedClips);
    }
  }
}
